import {
    Component,
    OnInit,
    AfterViewInit,
    OnDestroy,
    Input,
    HostBinding,
    Output,
    EventEmitter,
    Inject,
    ElementRef,
    forwardRef,
    ViewChild,
} from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';

export const ICC_SELECT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SelectComponent),
    multi: true,
};

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'icc-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    providers: [ICC_SELECT_CONTROL_VALUE_ACCESSOR],
})
export class SelectComponent implements OnInit, ControlValueAccessor, Validator, AfterViewInit, OnDestroy {
    private _disabled = false;
    _value: string | number = '';
    private _required = false;
    private _name: string | null = null;
    private _supportText: string | null = null;
    private _size: 'large' | 'medium' | 'small' = 'small';
    private _prefixIcon: string | null = null;
    private _appearance: 'fill' | 'outline' = 'fill';
    private _treatDefaultOptionAsValue = false;

    @ViewChild('select') select!: ElementRef<HTMLElement>;

    supportTextIsVisible = false;
    focused = false;

    @Input()
    @HostBinding('class.icc-disabled')
    get disabled() {
        return this._disabled;
    }
    set disabled(value: any) {
        this._disabled = coerceBooleanProperty(value);
    }

    @HostBinding('attr.disabled')
    get nativeDisabled(): boolean {
        return this.disabled;
    }

    @Input()
    @HostBinding('value')
    get value(): string | number {
        return this._value;
    }
    set value(value: string | number) {
        if(this._value !== value){
            this._value = value;
            this.handleElementsVisible();
        }
    }

    @Input()
    @HostBinding('attr.required')
    get required(): boolean {
        return this._required;
    }
    set required(required: boolean) {
        this._required = required;
    }

    @Input()
    @HostBinding('attr.name')
    get name(): string | null {
        return this._name;
    }
    set name(name: string | null) {
        this._name = name;
    }

    @Input()
    get supportText(): string | null {
        return this._supportText;
    }
    set supportText(text: string | null) {
        this._supportText = text;
    }

    @Input()
    get prefixIcon(): string | null {
        return this._prefixIcon;
    }
    set prefixIcon(prefixIcon: string | null) {
        this._prefixIcon = prefixIcon;
    }


    @Input()
    get size() {
        return this._size;
    }
    set size(size: 'large' | 'medium' | 'small') {
        this._size = size;
    }

    @Input()
    get appearance() {
        return this._appearance;
    }
    set appearance(value: 'fill' | 'outline') {
        this._appearance = value;
    }

    @Input()
    get treatDefaultOptionAsValue() {
        return this._treatDefaultOptionAsValue;
    }
    set treatDefaultOptionAsValue(value: boolean) {
        this._treatDefaultOptionAsValue = value;
    }

    @Input() label: string | null = null;

    @Output()
    readonly valueChange = new EventEmitter<string | number>();

    @Output()
    readonly focusedChange = new EventEmitter<boolean>();

    @Output()
    readonly hoveredChange = new EventEmitter<boolean>();

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    _onTouched: () => any = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private _controlValueAccessorChangeFn: (value: any) => void = () => {};

    constructor(
        @Inject(ElementRef)
        private readonly _elementRef: ElementRef<HTMLElement>,
        private _focusMonitor: FocusMonitor,

    ) {}

    ngOnInit() {
        this.handleElementsVisible();
        this.handleSize();
        this.handleClasses();
    }

    ngAfterViewInit(){

        this._focusMonitor.monitor(this._elementRef, true).subscribe((focusOrigin: FocusOrigin) => {
            if (!focusOrigin) {
                // When a focused element becomes disabled, the browser *immediately* fires a blur event.
                // Angular does not expect events to be raised during change detection, so any state change
                // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
                // See https://github.com/angular/angular/issues/17793. To work around this, we defer
                // telling the form control it has been touched until the next tick.
                Promise.resolve().then(() => {
                    this._onTouched();
                });
            }
        });
    }

    ngOnDestroy(){

        this._focusMonitor.stopMonitoring(this._elementRef);
    }

    writeValue(value: any): void {
        this.value = value;
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    registerOnChange(fn: (_: any) => {}): void {
        this._controlValueAccessorChangeFn = fn;
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    registerOnTouched(fn: () => {}): void {
        this._onTouched = fn;
    }
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (this.required && !this._value) {
            return { required: true };
        }
        return null;
    }

    /** Focuses the checkbox. */
    focus(origin?: FocusOrigin, options?: FocusOptions): void {
        if(this.select){
            if (origin) {
                this._focusMonitor.focusVia(this.select, origin, options);
            } else {
                this.select.nativeElement.focus(options);
            }
        }
    }

    protected handleSize() {
        if (this._elementRef?.nativeElement?.classList?.value) {
            const classes = this._elementRef?.nativeElement?.classList;
            if (classes.value.includes('icc-size-large')) {
                this._size = 'large';
            }
            if (classes.value.includes('icc-size-medium')) {
                this._size = 'medium';
            }
            if (classes.value.includes('icc-size-small')) {
                this._size = 'small';
            }
        }
    }

    protected handleClasses() {
        if (this._elementRef?.nativeElement) {
            const element = this._elementRef.nativeElement as HTMLElement;

            element.classList.add('icc-size-' + this._size);

            if (this._disabled) {
                element.classList.add('icc-disabled');
            }

            if (element?.parentElement?.classList?.value) {
                const classes = element.parentElement.classList;
                if (classes.value.includes('icc-form-field-frame')) {
                    element.classList.add('in-form-field');
                }
                if (classes.value.includes('has-icon')) {
                    element.classList.add('parent-has-icon');
                }
            }
        }
    }

    protected handleElementsVisible() {
        this.supportTextIsVisible = this._supportText ? true : false;
    }

    clearValue() {
        this._value = '';
        this.handleChange();
    }

    onChange(event: Event){
        event.stopPropagation();
        this._controlValueAccessorChangeFn(this._value);
        this.handleChange();
        this.handleFocused(true);
    }

    onClick(event: Event){
        event.stopPropagation();
        this.handleFocused(true);
    }

    handleChange() {
        this.handleElementsVisible();
        this.valueChange.emit(this._value);
    }

    handleFocused(focused: boolean) {
        this.focused = focused;
        this.focusedChange.emit(focused);
    }

    isInvalid(): boolean {
        return this.required && !this._value;
    }

    isValid(): boolean {
        return this.required && !!this._value;
    }
}
